import React, { useState } from "react";
import { View, StyleSheet, TouchableOpacity } from "react-native";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomSpacing from "../../common/layout/CustomSpacing/CustomSpacing";
import { ActivityAnalytics, ObjectiveAnalytics, ProgressDetails } from "../../../graphql/generated/graphql";

interface ActivityPanelProps {
  activity?: ActivityAnalytics;
  index?: number;
  inactive?: boolean;
  loading?: boolean;
}

// Utility functions
const formatTime = (seconds: number): string => {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${Math.round(seconds / 3600)}h`;
};

const formatPercentage = (value: number): string => {
  return `${Math.round(value)}%`;
};

const getSentimentColor = (sentiment: string): string => {
  switch (sentiment?.toLowerCase()) {
    case "positive":
      return "#10B981"; // green
    case "negative":
      return "#EF4444"; // red
    case "neutral":
    default:
      return "#6B7280"; // gray
  }
};

// Metric badge component
const StatBadge: React.FC<{
  label: string;
  value: string | number;
  color: string;
}> = ({ label, value, color }) => (
  <View style={[styles.statBadge, { backgroundColor: color + "20", borderColor: color }]}>
    <CustomText text={`${label}: ${value}`} size="xs" style={{ color }} useTranslationText={false} />
  </View>
);

// Progress bar component
const ProgressBar: React.FC<{
  percentage: number;
  size?: "sm" | "md" | "lg";
}> = ({ percentage, size = "sm" }) => {
  const height = size === "sm" ? 6 : size === "md" ? 8 : 12;
  const progressColor = "#3B82F6"; // blue

  return (
    <View style={styles.progressContainer}>
      <View style={[styles.progressBackground, { height }]}>
        <View
          style={[
            styles.progressFill,
            {
              height,
              width: `${Math.min(percentage, 100)}%`,
              backgroundColor: progressColor,
            },
          ]}
        />
      </View>
    </View>
  );
};

// ObjectivePanel component with expandable progress details
const ObjectivePanel: React.FC<{
  objective: ObjectiveAnalytics;
}> = ({ objective }) => {
  const [expanded, setExpanded] = useState(false);
  const [progressDetails, setProgressDetails] = useState<ProgressDetails | null>(null);
  const [loading, setLoading] = useState(false);

  const handleExpand = async () => {
    if (!expanded && !progressDetails) {
      setLoading(true);
      // Use ONLY the real progress_details from the objective data - NO MOCK DATA
      const realProgressDetails = objective.progress_details;
      setTimeout(() => {
        setProgressDetails(realProgressDetails);
        setLoading(false);
        setExpanded(true);
      }, 100);
    } else {
      setExpanded(!expanded);
    }
  };

  const hasDetailedData = objective.success_points > 0 || objective.confusion_points > 0;

  return (
    <View style={[styles.objectiveItem, { opacity: hasDetailedData ? 1 : 0.6 }]}>
      <TouchableOpacity style={styles.objectiveHeader} onPress={handleExpand} disabled={!hasDetailedData}>
        <View style={styles.objectiveHeaderContent}>
          <CustomText
            text={expanded ? "▼" : "▶"}
            size="xs"
            style={styles.objectiveExpandIcon}
            useTranslationText={false}
          />
          <CustomText
            text={objective.label}
            size="s"
            weight="bold"
            style={styles.objectiveLabel}
            useTranslationText={false}
          />
        </View>
      </TouchableOpacity>

      <View style={styles.objectiveMetrics}>
        <StatBadge label="Success" value={objective.success_points || 0} color="#10B981" />
        <StatBadge label="Confusion" value={objective.confusion_points || 0} color="#EF4444" />
        <StatBadge label="Questions" value={objective.progress_details?.questions_asked || 0} color="#3B82F6" />
        <StatBadge label="Interactions" value={objective.progress_details?.num_messages || 0} color="#8B5CF6" />
        <StatBadge label="Time" value={formatTime(objective.average_time_taken || 0)} color="#6B7280" />
      </View>

      {expanded && (
        <View style={styles.objectiveExpandedContent}>
          <CustomSpacing type="vertical" size="m" />

          {loading ? (
            <View style={styles.objectiveLoadingContainer}>
              <CustomText text="Loading progress details..." size="s" useTranslationText={false} />
            </View>
          ) : progressDetails ? (
            <View style={styles.progressDetailsContainer}>
              {/* Summary */}
              {progressDetails.summary && (
                <View style={styles.progressDetailSection}>
                  <View style={styles.progressDetailHeader}>
                    <CustomText text="📋" size="s" useTranslationText={false} />
                    <CustomText
                      text="Summary"
                      size="s"
                      weight="bold"
                      style={styles.progressDetailTitle}
                      useTranslationText={false}
                    />
                  </View>
                  <View style={styles.progressDetailContent}>
                    <CustomText text={progressDetails.summary} size="xs" useTranslationText={false} />
                  </View>
                </View>
              )}

              {/* Level & Analytics */}
              <View style={styles.progressStatsGrid}>
                {progressDetails.level && (
                  <View style={styles.progressStatItem}>
                    <CustomText text="Level" size="xs" style={styles.progressStatLabel} useTranslationText={false} />
                    <CustomText text={progressDetails.level} size="s" weight="bold" useTranslationText={false} />
                  </View>
                )}
                {progressDetails.sentiment_analysis && (
                  <View style={styles.progressStatItem}>
                    <CustomText
                      text="Sentiment"
                      size="xs"
                      style={styles.progressStatLabel}
                      useTranslationText={false}
                    />
                    <CustomText
                      text={progressDetails.sentiment_analysis}
                      size="s"
                      weight="bold"
                      useTranslationText={false}
                    />
                  </View>
                )}
                {progressDetails.originality_analysis && progressDetails.originality_analysis !== "0" && (
                  <View style={styles.progressStatItem}>
                    <CustomText
                      text="Originality"
                      size="xs"
                      style={styles.progressStatLabel}
                      useTranslationText={false}
                    />
                    <CustomText
                      text={progressDetails.originality_analysis}
                      size="s"
                      weight="bold"
                      useTranslationText={false}
                    />
                  </View>
                )}
                {progressDetails.average_student_question_complexity && (
                  <View style={styles.progressStatItem}>
                    <CustomText
                      text="Question Complexity"
                      size="xs"
                      style={styles.progressStatLabel}
                      useTranslationText={false}
                    />
                    <CustomText
                      text={progressDetails.average_student_question_complexity}
                      size="s"
                      weight="bold"
                      useTranslationText={false}
                    />
                  </View>
                )}
              </View>

              {/* Success Points Details */}
              {progressDetails.success_points && progressDetails.success_points.length > 0 && (
                <View style={styles.progressDetailSection}>
                  <View style={styles.progressDetailHeader}>
                    <CustomText text="✅" size="s" useTranslationText={false} />
                    <CustomText
                      text="Success Points"
                      size="s"
                      weight="bold"
                      style={styles.successProgressTitle}
                      useTranslationText={false}
                    />
                  </View>
                  <View style={styles.progressDetailsList}>
                    {progressDetails.success_points.map((point, i) => (
                      <View key={i} style={styles.successProgressItem}>
                        <CustomText text={`• ${point}`} size="xs" useTranslationText={false} />
                      </View>
                    ))}
                  </View>
                </View>
              )}

              {/* Confusion Points Details */}
              {progressDetails.confusion_points && progressDetails.confusion_points.length > 0 && (
                <View style={styles.progressDetailSection}>
                  <View style={styles.progressDetailHeader}>
                    <CustomText text="⚠️" size="s" useTranslationText={false} />
                    <CustomText
                      text="Confusion Points"
                      size="s"
                      weight="bold"
                      style={styles.confusionProgressTitle}
                      useTranslationText={false}
                    />
                  </View>
                  <View style={styles.progressDetailsList}>
                    {progressDetails.confusion_points.map((point, i) => (
                      <View key={i} style={styles.confusionProgressItem}>
                        <CustomText text={`• ${point}`} size="xs" useTranslationText={false} />
                      </View>
                    ))}
                  </View>
                </View>
              )}

              {/* Recommendations */}
              {progressDetails.further_recommendations && progressDetails.further_recommendations.length > 0 && (
                <View style={styles.progressDetailSection}>
                  <View style={styles.progressDetailHeader}>
                    <CustomText text="💡" size="s" useTranslationText={false} />
                    <CustomText
                      text="Recommendations"
                      size="s"
                      weight="bold"
                      style={styles.recommendationProgressTitle}
                      useTranslationText={false}
                    />
                  </View>
                  <View style={styles.progressRecommendationsGrid}>
                    {progressDetails.further_recommendations.map((rec, i) => (
                      <View key={i} style={styles.progressRecommendationChip}>
                        <CustomText
                          text={rec}
                          size="xs"
                          style={styles.progressRecommendationText}
                          useTranslationText={false}
                        />
                      </View>
                    ))}
                  </View>
                </View>
              )}

              {/* Distractions */}
              {progressDetails.distractions && progressDetails.distractions.length > 0 && (
                <View style={styles.progressDetailSection}>
                  <View style={styles.progressDetailHeader}>
                    <CustomText text="🔄" size="s" useTranslationText={false} />
                    <CustomText
                      text="Distractions"
                      size="s"
                      weight="bold"
                      style={styles.distractionProgressTitle}
                      useTranslationText={false}
                    />
                  </View>
                  <View style={styles.progressDetailsList}>
                    {progressDetails.distractions.map((distraction, i) => (
                      <View key={i} style={styles.distractionProgressItem}>
                        <CustomText text={`• ${distraction}`} size="xs" useTranslationText={false} />
                      </View>
                    ))}
                  </View>
                </View>
              )}

              {/* Message & Time Statistics */}
              <View style={styles.progressStatsGrid}>
                {progressDetails.num_messages && progressDetails.num_messages > 0 && (
                  <View style={styles.progressStatItem}>
                    <CustomText
                      text="Total Messages"
                      size="xs"
                      style={styles.progressStatLabel}
                      useTranslationText={false}
                    />
                    <CustomText
                      text={`${progressDetails.num_messages}`}
                      size="s"
                      weight="bold"
                      useTranslationText={false}
                    />
                  </View>
                )}
                {progressDetails.student_messages_count && progressDetails.student_messages_count > 0 && (
                  <View style={styles.progressStatItem}>
                    <CustomText
                      text="Student Messages"
                      size="xs"
                      style={styles.progressStatLabel}
                      useTranslationText={false}
                    />
                    <CustomText
                      text={`${progressDetails.student_messages_count}`}
                      size="s"
                      weight="bold"
                      useTranslationText={false}
                    />
                  </View>
                )}
                {progressDetails.ai_messages_count && progressDetails.ai_messages_count > 0 && (
                  <View style={styles.progressStatItem}>
                    <CustomText
                      text="AI Messages"
                      size="xs"
                      style={styles.progressStatLabel}
                      useTranslationText={false}
                    />
                    <CustomText
                      text={`${progressDetails.ai_messages_count}`}
                      size="s"
                      weight="bold"
                      useTranslationText={false}
                    />
                  </View>
                )}
                {progressDetails.questions_asked !== undefined && progressDetails.questions_asked > 0 && (
                  <View style={styles.progressStatItem}>
                    <CustomText
                      text="Questions Asked"
                      size="xs"
                      style={styles.progressStatLabel}
                      useTranslationText={false}
                    />
                    <CustomText
                      text={`${progressDetails.questions_asked}`}
                      size="s"
                      weight="bold"
                      useTranslationText={false}
                    />
                  </View>
                )}
                {progressDetails.average_student_response_time && progressDetails.average_student_response_time > 0 && (
                  <View style={styles.progressStatItem}>
                    <CustomText
                      text="Avg Student Response"
                      size="xs"
                      style={styles.progressStatLabel}
                      useTranslationText={false}
                    />
                    <CustomText
                      text={formatTime(progressDetails.average_student_response_time)}
                      size="s"
                      weight="bold"
                      useTranslationText={false}
                    />
                  </View>
                )}
                {progressDetails.average_ai_response_time && progressDetails.average_ai_response_time > 0 && (
                  <View style={styles.progressStatItem}>
                    <CustomText
                      text="Avg AI Response"
                      size="xs"
                      style={styles.progressStatLabel}
                      useTranslationText={false}
                    />
                    <CustomText
                      text={formatTime(progressDetails.average_ai_response_time)}
                      size="s"
                      weight="bold"
                      useTranslationText={false}
                    />
                  </View>
                )}
                {progressDetails.total_time_taken && progressDetails.total_time_taken > 0 && (
                  <View style={styles.progressStatItem}>
                    <CustomText
                      text="Total Time"
                      size="xs"
                      style={styles.progressStatLabel}
                      useTranslationText={false}
                    />
                    <CustomText
                      text={formatTime(progressDetails.total_time_taken)}
                      size="s"
                      weight="bold"
                      useTranslationText={false}
                    />
                  </View>
                )}
              </View>
            </View>
          ) : null}
        </View>
      )}
    </View>
  );
};

const ActivityPanel: React.FC<ActivityPanelProps> = ({ activity, index, inactive = false, loading = false }) => {
  const [expanded, setExpanded] = useState(false);

  // Format completion percentage correctly
  const formatCompletionPercentage = (value: number | undefined): string => {
    if (value === undefined || value === null) return formatPercentage(0);
    // If value is already in percentage form (> 1), don't multiply by 100
    return value > 1 ? formatPercentage(value) : formatPercentage(value * 100);
  };

  // Calculate completion for progress bar
  // TODO: This data point is not available. We need to get it from the backend. Right now completion_percentage is only calculate for objectives. that needs to be consolidated upstream to activities and then to modules.
  // const completionPercentage = activity?.completion_percentage
  //   ? activity.completion_percentage > 1
  //     ? activity.completion_percentage
  //     : activity.completion_percentage * 100
  //   : 0;
  const completionPercentage = 0;

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <CustomText text="Loading activity..." size="m" useTranslationText={false} />
      </View>
    );
  }

  return (
    <View style={[styles.container, { opacity: inactive ? 0.6 : 1 }]}>
      <TouchableOpacity style={styles.header} onPress={() => !inactive && setExpanded(!expanded)} disabled={inactive}>
        <View style={styles.headerLeft}>
          <CustomText text={expanded ? "▼" : "▶"} size="s" style={styles.expandIcon} useTranslationText={false} />
          <CustomText
            text={activity?.label || `Activity ${(index ?? 0) + 1}`}
            size="m"
            weight="bold"
            useTranslationText={false}
          />
          {activity?.sentiment && (
            <View
              style={[
                styles.sentimentBadge,
                {
                  backgroundColor: getSentimentColor(activity.sentiment) + "20",
                  borderColor: getSentimentColor(activity.sentiment),
                },
              ]}>
              <CustomText
                text={activity.sentiment}
                size="xs"
                style={{ color: getSentimentColor(activity.sentiment) }}
                useTranslationText={false}
              />
            </View>
          )}
        </View>
      </TouchableOpacity>

      {/* Always show progress bar and key metrics */}
      {completionPercentage > 0 && (
        <View style={styles.progressSection}>
          <View style={styles.progressHeader}>
            <CustomText text="Completion" size="xs" useTranslationText={false} />
            <CustomText text={formatCompletionPercentage(completionPercentage)} size="xs" useTranslationText={false} />
          </View>
          <ProgressBar percentage={completionPercentage} size="sm" />
        </View>
      )}

      <View style={styles.metricsRow}>
        <StatBadge label="Success" value={activity?.success_points || 0} color="#10B981" />
        <StatBadge label="Confusion" value={activity?.confusion_points || 0} color="#EF4444" />
        <StatBadge label="Questions" value={activity?.questions_asked || 0} color="#3B82F6" />
        {activity?.active_interaction_count && activity.active_interaction_count > 0 && (
          <StatBadge label="Interactions" value={activity.active_interaction_count} color="#8B5CF6" />
        )}
        {/* {activity?.total_time_taken && activity.total_time_taken > 0 && (
          <StatBadge label="Time" value={formatTime(activity.total_time_taken)} color="#6B7280" />
        )} */}
      </View>

      {expanded && (
        <View style={styles.expandedContent}>
          <CustomSpacing type="vertical" size="m" />

          {/* Summaries */}
          {activity?.summaries && activity.summaries.length > 0 && (
            <View style={styles.summariesSection}>
              <View style={styles.sectionHeader}>
                <CustomText text="📋" size="m" useTranslationText={false} />
                <CustomText
                  text="Summaries"
                  size="m"
                  weight="bold"
                  style={styles.sectionTitle}
                  useTranslationText={false}
                />
              </View>
              <CustomSpacing type="vertical" size="s" />
              {activity.summaries.map((summary, i) => (
                <View key={i} style={styles.summaryItem}>
                  <CustomText text={summary} size="s" useTranslationText={false} />
                </View>
              ))}
            </View>
          )}

          {/* Recommendations */}
          {activity?.further_recommendations && activity.further_recommendations.length > 0 && (
            <View style={styles.recommendationsSection}>
              <View style={styles.sectionHeader}>
                <CustomText text="💡" size="m" useTranslationText={false} />
                <CustomText
                  text="Recommendations"
                  size="m"
                  weight="bold"
                  style={styles.sectionTitle}
                  useTranslationText={false}
                />
              </View>
              <CustomSpacing type="vertical" size="s" />
              <View style={styles.recommendationsGrid}>
                {activity.further_recommendations.map((rec, i) => (
                  <View key={i} style={styles.recommendationChip}>
                    <CustomText text={rec} size="xs" style={styles.recommendationText} useTranslationText={false} />
                  </View>
                ))}
              </View>
            </View>
          )}

          {/* Success/Confusion Details */}
          {(activity?.success_points_details?.length || activity?.confusion_points_details?.length) && (
            <View style={styles.detailsSection}>
              {activity.success_points_details && activity.success_points_details.length > 0 && (
                <View style={styles.successDetailsSection}>
                  <View style={styles.sectionHeader}>
                    <CustomText text="📋" size="m" useTranslationText={false} />
                    <CustomText
                      text="Success Details"
                      size="m"
                      weight="bold"
                      style={styles.successTitle}
                      useTranslationText={false}
                    />
                  </View>
                  <CustomSpacing type="vertical" size="s" />
                  {activity.success_points_details.map((detail, i) => (
                    <View key={i} style={styles.successDetailItem}>
                      <CustomText text={detail} size="s" useTranslationText={false} />
                    </View>
                  ))}
                </View>
              )}

              {activity.confusion_points_details && activity.confusion_points_details.length > 0 && (
                <View style={styles.confusionDetailsSection}>
                  <View style={styles.sectionHeader}>
                    <CustomText text="⚠️" size="m" useTranslationText={false} />
                    <CustomText
                      text="Confusion Details"
                      size="m"
                      weight="bold"
                      style={styles.confusionTitle}
                      useTranslationText={false}
                    />
                  </View>
                  <CustomSpacing type="vertical" size="s" />
                  {activity.confusion_points_details.map((detail, i) => (
                    <View key={i} style={styles.confusionDetailItem}>
                      <CustomText text={detail} size="s" useTranslationText={false} />
                    </View>
                  ))}
                </View>
              )}
            </View>
          )}

          {/* Objectives */}
          <View style={styles.objectivesSection}>
            <CustomText
              text="Objectives"
              size="m"
              weight="bold"
              style={styles.objectivesTitle}
              useTranslationText={false}
            />
            <CustomSpacing type="vertical" size="s" />

            {activity?.details?.objectives && activity.details.objectives.length > 0 ? (
              <View style={styles.objectivesList}>
                {activity.details.objectives.map((objective, idx) => (
                  <ObjectivePanel key={objective.id || idx} objective={objective} />
                ))}
              </View>
            ) : (
              <CustomText
                text="No objectives for this activity."
                size="s"
                style={styles.noDataText}
                useTranslationText={false}
              />
            )}
          </View>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 12,
    marginLeft: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    backgroundColor: "#FFFFFF",
    padding: 12,
  },
  loadingContainer: {
    padding: 16,
    alignItems: "center",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  expandIcon: {
    marginRight: 8,
    color: "#6B7280",
  },
  sentimentBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 12,
    borderWidth: 1,
    marginLeft: 8,
  },
  progressSection: {
    marginTop: 8,
  },
  progressHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  progressContainer: {
    marginBottom: 4,
  },
  progressBackground: {
    backgroundColor: "#E5E7EB",
    borderRadius: 3,
    overflow: "hidden",
  },
  progressFill: {
    borderRadius: 3,
  },
  metricsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
    marginTop: 8,
  },
  statBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 8,
    borderWidth: 1,
    marginRight: 4,
    marginBottom: 4,
  },
  expandedContent: {
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
    paddingTop: 12,
    marginTop: 12,
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
  },
  sectionTitle: {
    marginLeft: 8,
    color: "#3B82F6",
  },
  summariesSection: {
    marginBottom: 16,
  },
  summaryItem: {
    backgroundColor: "#EBF8FF",
    borderLeftWidth: 4,
    borderLeftColor: "#3B82F6",
    padding: 8,
    marginBottom: 4,
    borderRadius: 4,
  },
  recommendationsSection: {
    marginBottom: 16,
  },
  recommendationsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  recommendationChip: {
    backgroundColor: "#F0FDF4",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#10B981",
  },
  recommendationText: {
    color: "#059669",
    fontWeight: "500",
  },
  detailsSection: {
    marginBottom: 16,
  },
  successDetailsSection: {
    marginBottom: 12,
  },
  successTitle: {
    color: "#059669",
  },
  successDetailItem: {
    backgroundColor: "#F0FDF4",
    borderLeftWidth: 4,
    borderLeftColor: "#10B981",
    padding: 8,
    marginBottom: 4,
    borderRadius: 4,
  },
  confusionDetailsSection: {
    marginBottom: 12,
  },
  confusionTitle: {
    color: "#DC2626",
  },
  confusionDetailItem: {
    backgroundColor: "#FEF2F2",
    borderLeftWidth: 4,
    borderLeftColor: "#EF4444",
    padding: 8,
    marginBottom: 4,
    borderRadius: 4,
  },
  objectivesSection: {
    marginBottom: 16,
  },
  objectivesTitle: {
    color: "#3B82F6",
  },
  objectivesList: {
    gap: 8,
  },
  objectiveItem: {
    backgroundColor: "#F9FAFB",
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 6,
    padding: 12,
  },
  objectiveHeader: {
    marginBottom: 8,
  },
  objectiveLabel: {
    color: "#374151",
  },
  objectiveMetrics: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
  },
  noDataText: {
    color: "#6B7280",
    fontStyle: "italic",
    paddingVertical: 8,
  },
  // ObjectivePanel styles
  objectiveHeaderContent: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  objectiveExpandIcon: {
    marginRight: 8,
    color: "#6B7280",
  },
  objectiveExpandedContent: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
  },
  objectiveLoadingContainer: {
    padding: 16,
    alignItems: "center",
  },
  progressDetailsContainer: {
    backgroundColor: "#FAFAFA",
    borderRadius: 8,
    padding: 12,
  },
  progressDetailSection: {
    marginBottom: 16,
  },
  progressDetailHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
  },
  progressDetailTitle: {
    marginLeft: 8,
    color: "#3B82F6",
  },
  progressDetailContent: {
    backgroundColor: "#FFFFFF",
    padding: 12,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  progressStatsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 16,
  },
  progressStatItem: {
    backgroundColor: "#FFFFFF",
    padding: 8,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    minWidth: 100,
    alignItems: "center",
  },
  progressStatLabel: {
    color: "#6B7280",
    marginBottom: 4,
  },
  successProgressTitle: {
    marginLeft: 8,
    color: "#059669",
  },
  progressDetailsList: {
    gap: 4,
  },
  successProgressItem: {
    backgroundColor: "#F0FDF4",
    borderLeftWidth: 4,
    borderLeftColor: "#10B981",
    padding: 8,
    borderRadius: 4,
  },
  confusionProgressTitle: {
    marginLeft: 8,
    color: "#DC2626",
  },
  confusionProgressItem: {
    backgroundColor: "#FEF2F2",
    borderLeftWidth: 4,
    borderLeftColor: "#EF4444",
    padding: 8,
    borderRadius: 4,
  },
  recommendationProgressTitle: {
    marginLeft: 8,
    color: "#059669",
  },
  progressRecommendationsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  progressRecommendationChip: {
    backgroundColor: "#F0FDF4",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#10B981",
  },
  progressRecommendationText: {
    color: "#059669",
    fontWeight: "500",
  },
  distractionProgressTitle: {
    marginLeft: 8,
    color: "#F59E0B",
  },
  distractionProgressItem: {
    backgroundColor: "#FFFBEB",
    borderLeftWidth: 4,
    borderLeftColor: "#F59E0B",
    padding: 8,
    borderRadius: 4,
  },
});

export default ActivityPanel;
